import React, { useEffect, useState } from "react";
import { User } from "../../../core/types/types";
import { useSelector } from "react-redux";
import apiInstance from "../../../utility/apiInstance";
import { ALPHA_REGEX, stripNonAlpha, stripNonNumeric } from "../../../utility/inputValidators";

interface PopupProps {
    show: boolean;
    onClose: () => void;
    addAccountManagerHandler: (accountManager: User) => void;
}

const AddAccountManagerPopup: React.FC<PopupProps> = ({
    show,
    onClose,
    addAccountManagerHandler,
}) => {
    const user = useSelector((state: any) => state.auth.user);

    const [msg, setMsg] = useState({
        error: false,
        loading: false,
        text: "",
    });

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
    });

    const [errorField, setErrorField] = useState('');

    useEffect(() => {
        if (show) {
            setFormData({ firstName: "", lastName: "", email: "", phone: "" });
            setMsg({ error: false, loading: false, text: "" });
            setErrorField('');
        }
    }, [show]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name } = e.target;
        let { value } = e.target;
        if (name === 'firstName' || name === 'lastName') {
            value = stripNonAlpha(value);
        } else if (name === 'phone') {
            value = stripNonNumeric(value);
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errorField === name) setErrorField('');
    };

    function generateRandomPassword(length = 12) {
        const charset =
            "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~";
        let password = "";
        for (let i = 0; i < length; i++) {
            password += charset.charAt(Math.floor(Math.random() * charset.length));
        }
        return password;
    }

    const validate = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{7,15}$/;

        if (!formData.firstName.trim() || !ALPHA_REGEX.test(formData.firstName)) {
            setErrorField('firstName');
            setMsg({ error: true, loading: false, text: "Valid first name is required" });
            return false;
        }
        if (!formData.lastName.trim() || !ALPHA_REGEX.test(formData.lastName)) {
            setErrorField('lastName');
            setMsg({ error: true, loading: false, text: "Valid last name is required" });
            return false;
        }
        if (!formData.email.trim() || !emailRegex.test(formData.email)) {
            setErrorField('email');
            setMsg({ error: true, loading: false, text: "Valid email is required" });
            return false;
        }
        if (!formData.phone.trim() || !phoneRegex.test(formData.phone)) {
            setErrorField('phone');
            setMsg({ error: true, loading: false, text: "Valid phone number is required (7–15 digits)" });
            return false;
        }
        setErrorField('');
        return true;
    };

    const saveAccountManager = async () => {
        if (!validate()) return;

        setMsg({ error: false, loading: true, text: "" });

        try {
            const res = await apiInstance.post(`/api/users`, {
                ...formData,
                role: "account_manager",
                recruiterManager: user.id,
                password: generateRandomPassword(),
            });

            addAccountManagerHandler(res.data.doc);
            setMsg({ error: false, loading: false, text: "Account manager added successfully!" });
            setTimeout(onClose, 2500);
        } catch (err: any) {
            const errorMsg =
                err?.response?.data?.errors?.[0]?.message ||
                err?.message ||
                "Failed to add account manager";
            setMsg({ error: true, loading: false, text: errorMsg });
        }
    };

    return (
        <div
            className={`modal fade ${show ? "show d-block" : ""}`}
            tabIndex={-1}
            role="dialog"
            style={{ background: show ? "rgba(0,0,0,0.5)" : "transparent" }}
            id="add-account-manager-pp"
        >
            <div className="modal-dialog-centered w-50 m-auto" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title text-secondary">Add Account Manager</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body">
                        <div className="card mb-4 shadow-sm border-0 rounded-3">
                            <div className="card-body">
                                <form>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    First Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errorField === 'firstName' ? 'is-invalid' : ''}`}
                                                    name="firstName"
                                                    value={formData.firstName}
                                                    onChange={handleInputChange}
                                                />
                                                {errorField === 'firstName' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Last Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errorField === 'lastName' ? 'is-invalid' : ''}`}
                                                    name="lastName"
                                                    value={formData.lastName}
                                                    onChange={handleInputChange}
                                                />
                                                {errorField === 'lastName' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Email <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    className={`form-control ${errorField === 'email' ? 'is-invalid' : ''}`}
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                />
                                                {errorField === 'email' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Phone Number <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errorField === 'phone' ? 'is-invalid' : ''}`}
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                />
                                                {errorField === 'phone' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-muted fs-13 mb-0">
                                        The account manager will receive a welcome email with their login credentials.
                                    </p>
                                </form>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer d-flex gap-3">
                        {msg.loading && (
                            <span className="mt-3 fs-14 fw-normal d-flex align-items-center justify-content-center">
                                Saving account manager...
                            </span>
                        )}
                        {!msg.loading && !errorField && msg.text && (
                            <span
                                className={`mt-3 ${msg.error ? "text-danger" : "text-success"} fs-14 fw-normal d-flex align-items-center justify-content-center`}
                            >
                                {msg.text}
                            </span>
                        )}
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={saveAccountManager}
                            disabled={msg.loading}
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddAccountManagerPopup;
